import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FACTOR_DATA } from '../data/factorInterpretations';
import { getAnswerOptions } from '../utils/answerFormatHelper';
import LoadingSpinner from '../components/LoadingSpinner';
import { initializeAssessment } from '../services/assessmentService';

const QuizPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const factorNumber = parseInt(searchParams.get('factor')) || 1;

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [factorData, setFactorData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [canAccess, setCanAccess] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  useEffect(() => {
    loadQuizData();
  }, [currentUser, factorNumber]);

  const loadQuizData = async () => {
    try {
      setLoading(true);

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        navigate('/couple/dashboard');
        return;
      }

      const user = userDoc.data();
      setUserData(user);

      // Check if approved
      if (user.approvalStatus !== 'approved') {
        navigate('/couple/dashboard');
        return;
      }

      // Get couple data to check permissions
      const coupleDoc = await getDoc(doc(db, 'couples', user.coupleId));
      if (!coupleDoc.exists()) {
        navigate('/couple/dashboard');
        return;
      }

      const coupleData = coupleDoc.data();
      const permissions = coupleData.factorPermissions || {};

      // Check if couple can take this factor
      if (!permissions[factorNumber]?.canTake) {
        alert('This factor is not yet unlocked by your counselor.');
        navigate('/couple/dashboard');
        return;
      }

      // Get assessment data
      const assessmentDoc = await getDoc(doc(db, 'assessments', currentUser.uid));
      let assessmentData = null;

      if (assessmentDoc.exists()) {
        assessmentData = assessmentDoc.data();

        // Check if already completed
        if (assessmentData.completedFactors?.includes(factorNumber)) {
          setAlreadyCompleted(true);
          setLoading(false);
          return;
        }

        // Load existing answers for this factor
        const existingAnswers = {};
        const factor = FACTOR_DATA[factorNumber];
        
        if (factor && factor.questions) {
          factor.questions.forEach(q => {
            if (assessmentData.answers?.[q.id] !== undefined) {
              existingAnswers[q.id] = assessmentData.answers[q.id];
            }
          });
        }

        setAnswers(existingAnswers);
      } else {
        // Initialize assessment if doesn't exist
        const { success, data, error } = await initializeAssessment(
         currentUser.uid, 
         user.coupleId
       );
  
       if (!success) {
         console.error('Error initializing assessment:', error);
         alert('Error creating assessment. Please try again.');
         navigate('/couple/dashboard');
         return;
       }
     }

      // Load factor data
      const factor = FACTOR_DATA[factorNumber];
      if (!factor) {
        alert('Factor not found.');
        navigate('/couple/dashboard');
        return;
      }

      setFactorData(factor);
      setCanAccess(true);
      setLoading(false);
    } catch (error) {
      console.error('Error loading quiz data:', error);
      alert('Error loading quiz. Please try again.');
      navigate('/couple/dashboard');
    }
  };

  const handleAnswer = async (value) => {
    try {
      const currentQuestion = factorData.questions[currentQuestionIndex];
      const questionId = currentQuestion.id;

      // Update local state
      const updatedAnswers = {
        ...answers,
        [questionId]: value
      };
      setAnswers(updatedAnswers);

      // Save to database
      await setDoc(
        doc(db, 'assessments', currentUser.uid),
        {
          [`answers.${questionId}`]: value,
          lastQuestion: questionId,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      // Move to next question or finish
      if (currentQuestionIndex < factorData.questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Last question - calculate score and complete factor
        await completeFactor(updatedAnswers);
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      alert('Error saving answer. Please try again.');
    }
  };

  const completeFactor = async (finalAnswers) => {
    try {
      // Calculate factor score
      let factorScore = 0;
      factorData.questions.forEach(q => {
        factorScore += finalAnswers[q.id] || 0;
      });

      // Get current assessment to calculate total
      const assessmentDoc = await getDoc(doc(db, 'assessments', currentUser.uid));
      const currentData = assessmentDoc.data();
      const currentFactorScores = currentData.factorScores || {};
      currentFactorScores[factorNumber] = factorScore;

      // Calculate total score
      const totalScore = Object.values(currentFactorScores).reduce((sum, score) => sum + score, 0);

      // Update assessment with completion
      await updateDoc(doc(db, 'assessments', currentUser.uid), {
        [`factorScores.${factorNumber}`]: factorScore,
        completedFactors: arrayUnion(factorNumber),
        totalScore: totalScore,
        status: 'in-progress',
        updatedAt: serverTimestamp()
      });

      console.log(`✅ Factor ${factorNumber} completed with score: ${factorScore}/50`);

      // ✅ AUTO-UNLOCK NEXT FACTOR (Only if Factor > 1 and Factor < 20)
      if (userData?.coupleId && factorNumber >= 1 && factorNumber < 20) {
        const nextFactor = factorNumber + 1;
        const coupleRef = doc(db, 'couples', userData.coupleId);
        
        try {
          await updateDoc(coupleRef, {
            [`factorPermissions.${nextFactor}.canTake`]: true,
            [`factorPermissions.${nextFactor}.unlockedAt`]: serverTimestamp()
          });
          
          console.log(`✅ Auto-unlocked Factor ${nextFactor} (after completing Factor ${factorNumber})`);
          alert(`Factor ${factorNumber} completed! Score: ${factorScore}/50. Factor ${nextFactor} has been unlocked.`);
        } catch (unlockError) {
          console.error(`Error auto-unlocking Factor ${nextFactor}:`, unlockError);
          alert(`Factor ${factorNumber} completed! Score: ${factorScore}/50. Your counselor will review your responses.`);
        }
      } else if (factorNumber === 20) {
        console.log(`🎉 All 20 factors completed!`);
        alert(`Congratulations! You've completed all 20 factors! Total Score: ${totalScore}/1000`);
      } else {
        alert(`Factor ${factorNumber} completed! Score: ${factorScore}/50. Your counselor will review your responses.`);
      }

      navigate('/couple/dashboard');
    } catch (error) {
      console.error('Error completing factor:', error);
      alert('Error completing factor. Please try again.');
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < factorData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading questions..." />;
  }

  if (alreadyCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
          <div className="text-4xl sm:text-5xl md:text-6xl mb-4">✅</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Factor Already Completed
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            You have already completed Factor {factorNumber}. Your counselor is reviewing your responses.
          </p>
          <button
            onClick={() => navigate('/couple/dashboard')}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!canAccess || !factorData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
          <div className="text-4xl sm:text-5xl md:text-6xl mb-4">🔒</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            You don't have permission to access this factor yet.
          </p>
          <button
            onClick={() => navigate('/couple/dashboard')}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = factorData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / factorData.questions.length) * 100;
  const currentAnswer = answers[currentQuestion.id];
  const allAnswered = factorData.questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-3xl mx-auto py-4 sm:py-6 md:py-8">
        {/* Header */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
                Factor {factorNumber}: {factorData.name}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Question {currentQuestionIndex + 1} of {factorData.questions.length}
              </p>
            </div>
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="px-3 sm:px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              Exit
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
            <div
              className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 sm:h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Navigation Dots */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex justify-center gap-2 flex-wrap">
            {factorData.questions.map((q, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full text-sm font-medium transition ${
                  answers[q.id] !== undefined
                    ? 'bg-green-500 text-white'
                    : currentQuestionIndex === index
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-5 sm:p-6 md:p-8">
          {/* Question Text */}
          <div className="mb-6 sm:mb-8">
            <div className="text-xs sm:text-sm text-purple-600 font-medium mb-2">
              Question {currentQuestionIndex + 1}
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 leading-relaxed">
              {currentQuestion.text}
            </h2>
          </div>

          {/* Answer Options */}
          <div className="space-y-2 sm:space-y-3">
            {getAnswerOptions(currentQuestion).map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                className={`w-full p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 text-left font-medium transition-all ${
                  currentAnswer === option.value
                    ? 'border-purple-600 bg-purple-50 text-purple-900'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm sm:text-base">{option.text}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs sm:text-sm text-gray-500">
                      {option.value} pt{option.value !== 1 ? 's' : ''}
                    </span>
                    {currentAnswer === option.value && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6 sm:mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="w-full sm:flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              ← Previous
            </button>
            {currentQuestionIndex === factorData.questions.length - 1 ? (
              <button
                onClick={() => completeFactor(answers)}
                disabled={!allAnswered}
                className={`w-full sm:flex-1 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                  allAnswered
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {allAnswered ? '✓ Submit Quiz' : `Answer all questions (${Object.keys(answers).length}/${factorData.questions.length})`}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="w-full sm:flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Next →
              </button>
            )}
          </div>

          {/* Why This Matters - Insight */}
          {currentQuestion.insight && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs sm:text-sm text-yellow-800">
                💡 <strong>Why this matters:</strong> {currentQuestion.insight}
              </p>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-4 sm:mt-6 bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-5 md:p-6">
          <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">📋 Instructions</h3>
          <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
            <li>• Answer all {factorData.questions.length} questions in this factor</li>
            <li>• Click on the numbered circles above to jump to any question</li>
            <li>• Green circles = answered, Purple = current question</li>
            <li>• Your responses are saved automatically</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;